/** @mui/material imports */
/** @mui/icons-material imports */
/** React imports */
import React, {
  useEffect,
  useContext,
  createContext,
  useState,
  useCallback,
} from 'react';
import { useParams } from 'react-router-dom';
/** App imports */
import { DrillholeContext } from '../../Contexts/DrillholeContext';
import { DrillHoleObject, ValidatorProps } from '../../TypeScript/interfaces';
import NoMatch from '../../Components/404';

export const ActiveDrillholeContext = createContext<{
  previousVerifiedDrillhole: DrillHoleObject | null;
  nextVerifiedDrillhole: DrillHoleObject | null;
  verifiedDrillhole: DrillHoleObject | null;
}>({
  previousVerifiedDrillhole: null,
  nextVerifiedDrillhole: null,
  verifiedDrillhole: null,
});

/**
 * Validate the drillhole id in the path and provide context
 *
 * Note: Assumes project id is verified
 *
 * @param props standard validator props
 * @returns child component with validation applie
 */
const DrillholeValidator: React.FC<ValidatorProps> = (
  props: React.PropsWithChildren<ValidatorProps>,
): JSX.Element => {
  const targetDrillholeId = useParams().drillholeId;
  const { drillholesMap, drillholesMapReady } = useContext(DrillholeContext);
  const [verifiedDrillholeId, setVerifiedDrillholeId] = useState<number>(-1);
  const [verifiedDrillhole, setVerifiedDrillhole] =
    useState<DrillHoleObject | null>(null);
  const [nextVerifiedDrillhole, setNextVerifiedDrillhole] =
    useState<DrillHoleObject | null>(null);
  const [previousVerifiedDrillhole, setPreviousVerifiedDrillhole] =
    useState<DrillHoleObject | null>(null);

  const validateDrillholeId = useCallback(
    (drillholeId: string): void => {
      if (!isNaN(Number(drillholeId))) {
        const drillholeIds: string[] = Object.keys(drillholesMap.byId);
        const targetDrillholeIndex: number = drillholeIds.indexOf(drillholeId);
        if (targetDrillholeIndex !== -1) {
          const verifiedDrillholeId: number =
            drillholesMap.byId[drillholeIds[targetDrillholeIndex]].id;

          setVerifiedDrillhole(
            drillholesMap.byId[drillholeIds[targetDrillholeIndex]] || null,
          );
          if (targetDrillholeIndex + 1 < drillholeIds.length) {
            setNextVerifiedDrillhole(
              drillholesMap.byId[drillholeIds[targetDrillholeIndex + 1]],
            );
          } else {
            setNextVerifiedDrillhole(null);
          }
          if (targetDrillholeIndex - 1 >= 0) {
            setPreviousVerifiedDrillhole(
              drillholesMap.byId[drillholeIds[targetDrillholeIndex - 1]],
            );
          } else {
            setPreviousVerifiedDrillhole(null);
          }
          handleValidDrillhole(verifiedDrillholeId);
        } else {
          handleInvalidDrillholeId();
        }
      } else {
        handleInvalidDrillholeId();
      }
    },
    [drillholesMap],
  );

  const handleInvalidDrillholeId = (): void => {
    setVerifiedDrillholeId(-2);
  };

  const handleValidDrillhole = (drillholeId: number): void => {
    setVerifiedDrillholeId(drillholeId);
  };

  // validate drillhole on init, when url changes, and when active project changes
  useEffect(() => {
    if (targetDrillholeId && drillholesMapReady) {
      validateDrillholeId(targetDrillholeId);
    }
  }, [drillholesMapReady, targetDrillholeId, validateDrillholeId]);

  return (
    <>
      {verifiedDrillholeId >= 0 && (
        <ActiveDrillholeContext.Provider
          value={{
            previousVerifiedDrillhole,
            nextVerifiedDrillhole,
            verifiedDrillhole,
          }}
        >
          {props.children}
        </ActiveDrillholeContext.Provider>
      )}
      {verifiedDrillholeId === -2 && <NoMatch />}
    </>
  );
};

export default DrillholeValidator;
